import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { Trash2, Plus, Minus } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import { useCart } from "@/hooks/useCart"
import { useDebounce } from "@/hooks/useDebounce"
import { formatToDevanagariStyle } from "@/utilities/helper"
import { cartService } from "@/api/cartService"

type DeliveryLocation = {
  location_id: number
  city: string
  district: string | null
  district_id: number | null
  country: string | null
  country_id: number | null
  fee: number
}

type LocationOption = { id: number; name: string }

/**
 * Cities whose district or country is missing would be unreachable once the
 * selects cascade, so they are grouped under this synthetic parent instead.
 */
const UNGROUPED_ID = 0
const UNGROUPED_NAME = "Other"

const groupOptions = (
  locations: DeliveryLocation[],
  idOf: (location: DeliveryLocation) => number | null,
  nameOf: (location: DeliveryLocation) => string | null
): LocationOption[] => {
  const options = new Map<number, string>()
  locations.forEach((location) => {
    const id = idOf(location) ?? UNGROUPED_ID
    if (!options.has(id)) {
      options.set(id, nameOf(location) ?? UNGROUPED_NAME)
    }
  })
  return [...options].map(([id, name]) => ({ id, name }))
}

export const Cart: React.FC = () => {
  const {
    cart,
    isLoading,
    removeItem,
    updateQuantityAsync,
    checkout,
    isCheckingOut,
  } = useCart()
  const navigate = useNavigate()
  const [localQuantities, setLocalQuantities] = useState<{
    [key: number]: number
  }>({})
  const [updatingItemId, setUpdatingItemId] = useState<number | null>(null)
  const lastSyncedQuantitiesRef = useRef<{ [key: number]: number }>({})

  const cartItems = cart?.items || []

  // Initialize local quantities from cart
  useEffect(() => {
    if (cartItems.length > 0) {
      const initialQuantities: { [key: number]: number } = {}
      cartItems.forEach((item: any) => {
        initialQuantities[item.id] = item.quantity
      })
      setLocalQuantities(initialQuantities)
      lastSyncedQuantitiesRef.current = { ...initialQuantities }
    }
  }, [cartItems.length]) // Only update when number of items changes

  // Debounce quantity updates
  const debouncedQuantities = useDebounce(localQuantities, 500)

  // Sync debounced quantities with server
  useEffect(() => {
    if (!cartItems.length || Object.keys(debouncedQuantities).length === 0)
      return

    const syncQuantities = async () => {
      for (const [cartItemIdStr, quantity] of Object.entries(
        debouncedQuantities
      )) {
        const cartItemId = parseInt(cartItemIdStr)
        const lastSynced = lastSyncedQuantitiesRef.current[cartItemId]
        const cartItem = cartItems.find((item: any) => item.id === cartItemId)

        if (
          cartItem &&
          quantity !== lastSynced &&
          quantity !== cartItem.quantity
        ) {
          setUpdatingItemId(cartItemId)
          try {
            await updateQuantityAsync({
              cartItemId: cartItemId,
              quantity: quantity,
            })
            lastSyncedQuantitiesRef.current[cartItemId] = quantity
          } catch (error) {
            // Error is handled in the hook, revert to server quantity
            setLocalQuantities((prev) => ({
              ...prev,
              [cartItemId]: cartItem.quantity,
            }))
            lastSyncedQuantitiesRef.current[cartItemId] = cartItem.quantity
          } finally {
            setUpdatingItemId(null)
          }
        }
      }
    }

    syncQuantities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuantities])

  const subtotal = cartItems.reduce((sum: number, item: any) => {
    const quantity = localQuantities[item.id] ?? item.quantity
    const price = Number(
      item.product?.discount_details?.final_price ??
        item.product?.sell_price ??
        0
    )
    return sum + price * quantity
  }, 0)

  const [selectedCountryId, setSelectedCountryId] = useState<number | "">("")
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | "">("")
  const [selectedLocationId, setSelectedLocationId] = useState<number | "">("")
  const [deliveryLandmark, setDeliveryLandmark] = useState("")
  const [deliveryPhone, setDeliveryPhone] = useState("")
  const [deliveryAlternativePhone, setDeliveryAlternativePhone] = useState("")
  const [phoneErrors, setPhoneErrors] = useState<{
    phone?: string
    alternativePhone?: string
  }>({})

  const { data: locationsResponse } = useQuery({
    queryKey: ["delivery-locations"],
    queryFn: ({ signal }) => cartService.getDeliveryLocations(signal),
  })
  const deliveryLocations: DeliveryLocation[] =
    (locationsResponse as any)?.data || []

  // The endpoint returns one flat row per deliverable city; narrow it down a
  // level at a time so each select only offers choices that lead to a city the
  // store actually delivers to.
  const countries = useMemo(
    () =>
      groupOptions(
        deliveryLocations,
        (location) => location.country_id,
        (location) => location.country
      ),
    [deliveryLocations]
  )

  const districts = useMemo(() => {
    if (selectedCountryId === "") return []
    return groupOptions(
      deliveryLocations.filter(
        (location) =>
          (location.country_id ?? UNGROUPED_ID) === selectedCountryId
      ),
      (location) => location.district_id,
      (location) => location.district
    )
  }, [deliveryLocations, selectedCountryId])

  const cities = useMemo(() => {
    if (selectedDistrictId === "") return []
    return deliveryLocations.filter(
      (location) =>
        (location.country_id ?? UNGROUPED_ID) === selectedCountryId &&
        (location.district_id ?? UNGROUPED_ID) === selectedDistrictId
    )
  }, [deliveryLocations, selectedCountryId, selectedDistrictId])

  // Don't make the customer pick from a list of one.
  useEffect(() => {
    if (selectedCountryId === "" && countries.length === 1) {
      setSelectedCountryId(countries[0].id)
    }
  }, [countries, selectedCountryId])

  useEffect(() => {
    if (selectedDistrictId === "" && districts.length === 1) {
      setSelectedDistrictId(districts[0].id)
    }
  }, [districts, selectedDistrictId])

  const handleCountryChange = (value: string) => {
    setSelectedCountryId(value === "" ? "" : Number(value))
    setSelectedDistrictId("")
    setSelectedLocationId("")
  }

  const handleDistrictChange = (value: string) => {
    setSelectedDistrictId(value === "" ? "" : Number(value))
    setSelectedLocationId("")
  }

  const selectedLocation = deliveryLocations.find(
    (location) => location.location_id === selectedLocationId
  )
  const deliveryFee = selectedLocation?.fee ?? 0
  const orderTotal = subtotal + deliveryFee

  const handleCheckout = async () => {
    if (!selectedLocationId) {
      toast.error("Please choose a delivery location")
      return
    }
    const phone = deliveryPhone.replace(/[\s()-]+/g, "")
    const alternativePhone = deliveryAlternativePhone.replace(/[\s()-]+/g, "")
    const errors: typeof phoneErrors = {}
    if (!phone) {
      errors.phone = "Please enter a phone number for delivery."
    } else if (!/^\+?[0-9]{7,15}$/.test(phone)) {
      errors.phone = "Enter 7 to 15 digits, with an optional + country code."
    }
    if (alternativePhone && !/^\+?[0-9]{7,15}$/.test(alternativePhone)) {
      errors.alternativePhone =
        "Enter 7 to 15 digits, with an optional + country code."
    }
    setPhoneErrors(errors)
    if (Object.keys(errors).length > 0) return
    try {
      await checkout({
        location_id: selectedLocationId,
        delivery_landmark: deliveryLandmark.trim() || undefined,
        delivery_phone: phone,
        delivery_alternative_phone: alternativePhone || undefined,
      })
      navigate("/orders")
    } catch (error) {
      // Error is handled in the hook
    }
  }

  const handleQuantityChange = useCallback(
    (cartItemId: number, delta: number) => {
      const cartItem = cartItems.find((item: any) => item.id === cartItemId)
      if (!cartItem) return

      const currentQuantity = localQuantities[cartItemId] ?? cartItem.quantity
      const maxQuantity = Number(
        cartItem.product?.available_stock ?? cartItem.product?.stock ?? 0
      )
      const newQuantity = Math.max(
        1,
        Math.min(maxQuantity, currentQuantity + delta)
      )

      setLocalQuantities((prev) => ({
        ...prev,
        [cartItemId]: newQuantity,
      }))
    },
    [cartItems, localQuantities]
  )

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen py-10 flex items-center justify-center">
        <div className="text-gray-500">Loading cart...</div>
      </div>
    )
  }

  if (!cart || cartItems.length === 0) {
    return (
      <div className="bg-gray-50 min-h-screen py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Shopping Cart
          </h1>
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">Your cart is empty</p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

        <div className="lg:grid lg:grid-cols-12 lg:gap-x-12 lg:items-start">
          {/* Cart Items List */}
          <section className="lg:col-span-7 bg-white rounded-lg shadow-sm p-6">
            <ul className="divide-y divide-gray-200">
              {cartItems.map((item: any) => {
                const displayQuantity =
                  localQuantities[item.id] ?? item.quantity
                const maxQuantity = Number(
                  item.product?.available_stock ?? item.product?.stock ?? 0
                )
                const price = Number(
                  item.product?.discount_details?.final_price ??
                    item.product?.sell_price ??
                    0
                )
                const itemTotal = price * displayQuantity
                const isUpdating = updatingItemId === item.id

                return (
                  <li key={item.id} className="flex py-6">
                    <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                      {item.product?.thumbnail ? (
                        <img
                          src={item.product.thumbnail.url}
                          alt={item.product.name}
                          className="h-full w-full object-cover object-center"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex flex-1 flex-col">
                      <div>
                        <div className="flex justify-between text-base font-medium text-gray-900">
                          <h3 className="text-md">
                            {item.product?.name || "Product"}
                          </h3>
                          <p className="ml-4">
                            Rs. {formatToDevanagariStyle(itemTotal)}
                          </p>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Rs. {formatToDevanagariStyle(price) || 0} each
                        </p>
                      </div>

                      <div className="flex flex-1 items-end justify-between text-sm mt-3">
                        <div className="flex items-center border rounded-md">
                          <button
                            onClick={() => handleQuantityChange(item.id, -1)}
                            disabled={isUpdating || displayQuantity <= 1}
                            className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Minus size={16} />
                          </button>
                          <span className="px-2 min-w-[24px] text-center flex items-center justify-center">
                            {displayQuantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item.id, 1)}
                            disabled={
                              isUpdating || displayQuantity >= maxQuantity
                            }
                            className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="font-medium text-red-600 hover:text-red-500 flex items-center"
                        >
                          <Trash2 size={16} className="mr-1" /> Remove
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>

          {/* Order Summary */}
          <section className="lg:col-span-5 mt-16 lg:mt-0 bg-white rounded-lg shadow-sm p-6 sticky top-24">
            <h2 className="text-lg font-medium text-gray-900">Order Summary</h2>

            {/* Delivery Location */}
            <div className="mt-6 space-y-3">
              <span className="block text-sm font-medium text-gray-700">
                Delivery location
              </span>

              <div>
                <label
                  htmlFor="delivery-country"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Country
                </label>
                <select
                  id="delivery-country"
                  value={selectedCountryId}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  disabled={countries.length === 0}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">Choose your country...</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="delivery-district"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  District
                </label>
                <select
                  id="delivery-district"
                  value={selectedDistrictId}
                  onChange={(e) => handleDistrictChange(e.target.value)}
                  disabled={selectedCountryId === ""}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">
                    {selectedCountryId === ""
                      ? "Choose a country first"
                      : "Choose your district..."}
                  </option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="delivery-city"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  City
                </label>
                <select
                  id="delivery-city"
                  value={selectedLocationId}
                  onChange={(e) =>
                    setSelectedLocationId(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  disabled={selectedDistrictId === ""}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">
                    {selectedDistrictId === ""
                      ? "Choose a district first"
                      : "Choose your city..."}
                  </option>
                  {cities.map((location) => (
                    <option
                      key={location.location_id}
                      value={location.location_id}
                    >
                      {location.city} — Rs.{" "}
                      {formatToDevanagariStyle(location.fee)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="delivery-landmark"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Delivery landmark or detailed location (optional)
                </label>
                <textarea
                  id="delivery-landmark"
                  value={deliveryLandmark}
                  onChange={(e) => setDeliveryLandmark(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder="For example: near the main gate, beside ABC Bank"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label
                  htmlFor="delivery-phone"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Phone number (required)
                </label>
                <input
                  id="delivery-phone"
                  name="delivery_phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  maxLength={30}
                  value={deliveryPhone}
                  onChange={(e) => {
                    setDeliveryPhone(e.target.value)
                    setPhoneErrors((prev) => ({ ...prev, phone: undefined }))
                  }}
                  aria-invalid={!!phoneErrors.phone}
                  aria-describedby={
                    phoneErrors.phone ? "delivery-phone-error" : undefined
                  }
                  placeholder="For example: 9801234567"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500"
                />
                {phoneErrors.phone && (
                  <p
                    id="delivery-phone-error"
                    role="alert"
                    className="mt-1 text-xs text-red-600"
                  >
                    {phoneErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="delivery-alternative-phone"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Alternative phone number (optional)
                </label>
                <input
                  id="delivery-alternative-phone"
                  name="delivery_alternative_phone"
                  type="tel"
                  maxLength={30}
                  value={deliveryAlternativePhone}
                  onChange={(e) => {
                    setDeliveryAlternativePhone(e.target.value)
                    setPhoneErrors((prev) => ({
                      ...prev,
                      alternativePhone: undefined,
                    }))
                  }}
                  aria-invalid={!!phoneErrors.alternativePhone}
                  aria-describedby={
                    phoneErrors.alternativePhone
                      ? "delivery-alternative-phone-error"
                      : undefined
                  }
                  placeholder="Another number we can contact"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500"
                />
                {phoneErrors.alternativePhone && (
                  <p
                    id="delivery-alternative-phone-error"
                    role="alert"
                    className="mt-1 text-xs text-red-600"
                  >
                    {phoneErrors.alternativePhone}
                  </p>
                )}
              </div>

              {deliveryLocations.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  No delivery locations available yet.
                </p>
              )}
            </div>

            <fieldset className="mt-6">
              <legend className="text-sm font-medium text-gray-700">
                Payment method
              </legend>
              <label className="mt-2 flex items-start gap-3 rounded-md border border-indigo-300 bg-indigo-50 p-3">
                <input
                  type="radio"
                  name="payment_method"
                  value="cod"
                  checked
                  readOnly
                  className="mt-1 h-4 w-4 accent-indigo-600"
                  aria-label="Cash on Delivery (COD)"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Cash on Delivery (COD)
                  </span>
                  <span className="block text-xs text-gray-600">
                    Pay cash when your order is delivered.
                  </span>
                </span>
              </label>
            </fieldset>

            <dl className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <dt className="text-sm text-gray-600">Subtotal</dt>
                <dd className="text-sm font-medium text-gray-900">
                  Rs. {formatToDevanagariStyle(subtotal)}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm text-gray-600">Delivery fee</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {selectedLocation
                    ? `Rs. ${formatToDevanagariStyle(deliveryFee)}`
                    : "—"}
                </dd>
              </div>
              <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                <dt className="text-base font-medium text-gray-900">
                  Order total
                </dt>
                <dd className="text-base font-medium text-gray-900">
                  Rs. {formatToDevanagariStyle(orderTotal)}
                </dd>
              </div>
            </dl>
            <div className="mt-6">
              <button
                onClick={handleCheckout}
                disabled={
                  isCheckingOut || cartItems.length === 0 || !selectedLocationId
                }
                className="w-full bg-indigo-600 border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingOut ? "Processing..." : "Checkout"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
